import { plural, round } from 'pages/inquiry-processor/utils/describe-units';

// The packaging subset of an EditableVariantRow — every field is the raw
// string as stored in the grid (empty string = unset).
export interface PackagingFields {
  baseUnit: string;
  itemUnit: string;
  netContent: string;
  packUnit: string;
  packSize: string;
}

function toPositiveNumber(raw: string): number | null {
  if (!raw.trim()) return null;
  const n = Number(raw);
  return Number.isFinite(n) ? n : null;
}

// Compact "1 carton × 12 bottles × 1 kg" summary for the family workspace
// row list, in place of the old chip soup. Degrades gracefully as fields go
// missing: only baseUnit → "kg"; baseUnit+netContent → "1 kg"; nothing known
// → '—'. Field semantics match describe-units.ts (baseUnit/itemUnit/
// netContent/packUnit/packSize).
export function packagingSummary(f: PackagingFields): string {
  const baseUnit = f.baseUnit.trim();
  const itemUnit = f.itemUnit.trim();
  const packUnit = f.packUnit.trim();
  const netContent = toPositiveNumber(f.netContent);
  const packSize = toPositiveNumber(f.packSize);

  const segments: string[] = [];
  if (packUnit) segments.push(`1 ${packUnit}`);
  if (packSize != null && itemUnit) segments.push(`${round(packSize)} ${plural(itemUnit, packSize)}`);
  else if (itemUnit) segments.push(itemUnit);
  if (netContent != null && baseUnit) segments.push(`${round(netContent)} ${baseUnit}`);
  else if (baseUnit) segments.push(baseUnit);

  return segments.length ? segments.join(' × ') : '—';
}

// A variant's packaging-relevant fields plus the bits needed to compose its
// display label — a superset of PackagingFields so callers can pass a
// MasterProduct (or the editable grid's row shape) straight through.
export interface VariantLabelFields {
  id: number;
  brand?: string | null;
  spec?: string | null;
  baseUnit: string;
  itemUnit?: string | null;
  netContent?: number | null;
  packUnit?: string | null;
  packSize?: number | null;
}

// "brand · spec · packagingSummary" label, shared by the workspace screen
// (suppliers-panel tooltip) and the supplier coverage modal so both render
// variants identically — falls back to `#id` when brand/spec/packaging are
// all empty.
export function variantLabel(v: VariantLabelFields): string {
  const packaging = packagingSummary({
    baseUnit: v.baseUnit ?? '',
    itemUnit: v.itemUnit ?? '',
    netContent: v.netContent != null ? String(v.netContent) : '',
    packUnit: v.packUnit ?? '',
    packSize: v.packSize != null ? String(v.packSize) : '',
  });
  const segments = [v.brand, v.spec, packaging === '—' ? null : packaging].filter(
    (segment): segment is string => !!segment && segment.trim() !== ''
  );
  return segments.length ? segments.join(' · ') : `#${v.id}`;
}

// Mirrors the netContent/packSize numeric rules from row-helpers'
// validateRows: netContent must be a positive number, packSize a positive
// whole number, when present. Empty is valid (not yet filled in).
export function packagingInvalid(f: PackagingFields): boolean {
  const netContentInvalid =
    f.netContent.trim() !== '' && !(Number.isFinite(Number(f.netContent)) && Number(f.netContent) > 0);
  const packSizeInvalid = f.packSize.trim() !== '' && !(Number.isInteger(Number(f.packSize)) && Number(f.packSize) > 0);
  return netContentInvalid || packSizeInvalid;
}
